/**
 * Leaguemate Scouting — pure league categorization (Tier 0, no I/O).
 *
 * Turns a raw Sleeper league object into the factual category fields the
 * info-first drill-down renders (NO interpretation). Reused by the "Other
 * leagues" facet and anywhere a type breakdown is shown.
 */

import { countSuperFlex, pprValue, tepValue } from "./scoutSimilarity.js";

const TYPE_LABEL = { 0: "redraft", 1: "keeper", 2: "dynasty" };

/** Factual one-league descriptor. */
export function categorizeLeague(league) {
  const s = league?.settings || {};
  const bestBall = s.best_ball === 1;
  const type = bestBall ? "best-ball" : (TYPE_LABEL[s.type] ?? "other");
  const sf = countSuperFlex(league?.roster_positions);
  const ppr = pprValue(league?.scoring_settings);
  const tep = tepValue(league?.scoring_settings) > 0;
  const fmt = [];
  fmt.push(sf >= 1 ? "SF" : "1QB");
  fmt.push(ppr >= 1 ? "PPR" : ppr >= 0.5 ? "0.5 PPR" : "non-PPR");
  if (tep) fmt.push("TEP");
  return {
    league_id: league?.league_id,
    name: league?.name || null,
    season: league?.season != null ? String(league.season) : null,
    type,                       // dynasty | keeper | redraft | best-ball | other
    bestBall,
    format: fmt.join(" · "),    // e.g. "SF · PPR · TEP"
    teams: league?.total_rosters ?? null,
    status: league?.status || null,
  };
}

/** Count leagues by category for the "12 dynasty · 3 redraft · 5 BB" breakdown. */
export function leagueTypeBreakdown(leagues) {
  const counts = { dynasty: 0, keeper: 0, redraft: 0, "best-ball": 0, other: 0 };
  for (const l of leagues || []) {
    const c = categorizeLeague(l);
    counts[c.bestBall ? "best-ball" : c.type] = (counts[c.bestBall ? "best-ball" : c.type] || 0) + 1;
  }
  return counts;
}

/** "8 dynasty · 2 keeper · 1 redraft · 1 best-ball" (omits zero categories). */
export function breakdownString(leagues) {
  const c = leagueTypeBreakdown(leagues);
  const order = ["dynasty", "keeper", "redraft", "best-ball", "other"];
  const parts = order.filter((k) => c[k] > 0).map((k) => `${c[k]} ${k}`);
  return parts.join(" · ") || "no other NFL leagues";
}
